import React, { useCallback, useMemo } from 'react';

import { ArrowRight, Edit, OverflowMenuHorizontal, TrashCan } from '@carbon/icons-react';
import { Card, Classes, Text } from '@varicent/components';

import IconButton from 'components/Buttons/IconButton/IconButton';
import TextButton from 'components/Buttons/TextButton/TextButton';
import { Menu } from 'components/menu/Menu';
import { MenuItem } from 'components/menu/MenuItem';
import Popover from 'components/Popover/Popover';

import ExpandableCustomHierarchyTags from 'app/components/TerritoryMap/TerritoryMapGrid/ExpandableCustomHierarchyTags';
import OrphanedHierarchiesCallout from 'app/components/TerritoryMap/TerritoryMapGrid/OrphanedHierarchiesCallout';

import { useDedicatedMapProvider } from 'app/contexts/dedicatedMapProvider';
import { useMapContextRedistributor } from 'app/contexts/MapContextRedistributor/mapContextRedistributorProvider';
import { useMapWorkerPostMessage } from 'app/contexts/mapWorkerContext';

import { SplitFeatures } from 'app/global/features';
import { PresetSegmentIds } from 'app/global/variables';

import { DefinitionFilterOperatorEnum } from 'app/graphql/generated/graphqlApolloTypes';

import useCanUser from 'app/hooks/useCanUser';
import useTreatment from 'app/hooks/useTreatment';

import { CollectionFilter, HierarchyType, SegmentEditorDialogModes, SegmentFilterClause } from 'app/models';

import block from 'utils/bem-css-modules';
import { reportAnalyticsAction } from 'utils/helpers/analyticsReporter';
import { segmentToCollectionFilter } from 'utils/helpers/segmentFilterUtils';
import { formatMessage } from 'utils/messages/utils';
import { UserAction } from 'utils/permissions/userActions';

import style from './SegmentCard.module.pcss';
const b = block(style);

export interface SegmentCardProps {
  name: string;
  id: number;
  territoriesCount: number | null;
  isLoadingTerritoriesCount: boolean;
  filter: SegmentFilterClause[];
  orphanCount: number;
  customHierarchyFilter: CollectionFilter<number>[];
  rootOrphanCount: number;
  isPresetSegment?: boolean;
}

const SegmentCard: React.FC<SegmentCardProps> = ({
  name,
  territoriesCount,
  id,
  filter,
  isLoadingTerritoriesCount,
  orphanCount,
  customHierarchyFilter,
  rootOrphanCount,
  isPresetSegment = false
}) => {
  const postMessage = useMapWorkerPostMessage();
  const { setSegmentEditorDialogOptions, setSelectedSegmentId } = useDedicatedMapProvider();
  const canModifySegment = useCanUser(UserAction.SEGMENTS_MODIFY) && !isPresetSegment;
  const [isGenerateFilterFromTerritoryEnabled] = useTreatment(SplitFeatures.GENERATE_FILTER_FROM_TERRITORY);

  const customHierarchies = useMemo(() => filter.flatMap((clause) => clause.hierarchies), [filter]);
  const allMatchAnyFilter = filter.every(
    (currentFilter) => currentFilter.operator === DefinitionFilterOperatorEnum.MATCH_ANY_CONDITION
  );

  const totalHierarchies = customHierarchies.length;

  const allHierarchiesDeleted = totalHierarchies === 0 && orphanCount > 0;
  const { primaryHierarchy } = useMapContextRedistributor();

  const segmentAsFilter = useMemo(() => segmentToCollectionFilter(filter), [filter]);

  const rootHierarchyDeleted = rootOrphanCount > 0;

  const createCalloutInfo = (
    title: string,
    body: string,
    areOrphansDismissable: boolean
  ): { title: string; body: string; areOrphansDismissable: boolean } => {
    return {
      title,
      body,
      areOrphansDismissable
    };
  };

  const isSegmentDisabled = isGenerateFilterFromTerritoryEnabled && filter.length === 0 && !isPresetSegment;

  const currentCalloutInfo = useMemo(() => {
    if (rootHierarchyDeleted) {
      return createCalloutInfo(
        formatMessage('INVALID_SEGMENT_CALLOUT_TITLE'),
        formatMessage('INVALID_SEGMENT_CALLOUT_BODY'),
        false
      );
    } else if (allHierarchiesDeleted) {
      return createCalloutInfo(
        formatMessage('SEGMENT_UPDATE_REQUIRED_TITLE'),
        formatMessage('SEGMENT_UPDATE_REQUIRED_MESSAGE'),
        false
      );
    } else if (orphanCount > 0) {
      const title =
        orphanCount === 1
          ? formatMessage('ORPHANED_HIERARCHIES_DELETED_SINGULAR', { orphanCount })
          : formatMessage('ORPHANED_HIERARCHIES_DELETED', { orphanCount });

      return createCalloutInfo(title, formatMessage('ORPHANED_HIERARCHIES_WARNING'), true);
    } else {
      return null;
    }
  }, [orphanCount, rootHierarchyDeleted, allHierarchiesDeleted]);

  const applySegment = useCallback(() => {
    setSelectedSegmentId(id);

    let presetSegmentType: 'simpleOnly' | 'allRules' | null = null;

    if (isPresetSegment) {
      if (id === PresetSegmentIds.simpleOnly) {
        presetSegmentType = 'simpleOnly';
      } else if (id === PresetSegmentIds.allRules) {
        presetSegmentType = 'allRules';
      }
    }

    reportAnalyticsAction('segmentApplied', {
      presetSegmentType
    });

    postMessage({ type: 'custom-hierarchy-filter-change', filter: segmentAsFilter });
  }, [customHierarchyFilter, segmentAsFilter]);

  const shouldRenderApplyButton =
    (totalHierarchies > 0 || filter.length === 0 || allMatchAnyFilter) &&
    !allHierarchiesDeleted &&
    !rootHierarchyDeleted &&
    !isSegmentDisabled;

  return (
    <Card className={b()} elevation={0}>
      <div className={b('cardContainer')}>
        <div className={b('header')}>
          <Text className={b('title')}>{name}</Text>
          {canModifySegment && (
            <Popover
              minimal
              placement="bottom-end"
              content={
                <Menu data-testid="segment-menu-actions">
                  <MenuItem
                    icon={<Edit />}
                    text={formatMessage('EDIT_SEGMENT')}
                    data-testid="edit-segment-action"
                    onClick={() =>
                      setSegmentEditorDialogOptions({
                        mode: SegmentEditorDialogModes.EDIT,
                        filter: segmentAsFilter,
                        segment: { segmentId: id, segmentName: name }
                      })
                    }
                  />
                  <MenuItem
                    className={b('deleteActionButton')}
                    onClick={() =>
                      setSegmentEditorDialogOptions({ mode: SegmentEditorDialogModes.DELETE, segmentId: id })
                    }
                    icon={<TrashCan />}
                    text={formatMessage('DELETE')}
                    data-testid="delete-segment-action"
                  />
                </Menu>
              }
            >
              <IconButton type="button" testId="segment-card-action-button" icon={<OverflowMenuHorizontal />} />
            </Popover>
          )}
        </div>
        <div className={b('infoContainer')}>
          <div className={b('info')}>
            {!allHierarchiesDeleted && (
              <div
                data-testid="territories-count"
                className={`${b('territoryCount')} ${isLoadingTerritoriesCount && Classes.SKELETON}`}
              >
                {formatMessage('TERRITORIES_COUNT', { count: territoriesCount ?? emDash })}
              </div>
            )}
            <div className={b('footer')}>
              {!allHierarchiesDeleted && rootOrphanCount === 0 && (
                <div className={b('tags')}>
                  <ExpandableCustomHierarchyTags
                    isAccountFirstMap={primaryHierarchy === HierarchyType.CustomerAccountHierarchy}
                    customHierarchies={customHierarchies}
                    customHierarchyFilters={filter}
                  />
                </div>
              )}
              {currentCalloutInfo && (
                <div className={b('orphanedHierarchiesCalloutContainer')}>
                  <OrphanedHierarchiesCallout
                    segmentId={id}
                    segmentName={name}
                    filter={filter}
                    calloutBody={currentCalloutInfo.body}
                    calloutTitle={currentCalloutInfo.title}
                    areOrphansDismissable={currentCalloutInfo.areOrphansDismissable}
                  />
                </div>
              )}
              {shouldRenderApplyButton && (
                <div className={b('applyButton')}>
                  <TextButton
                    large={false}
                    rightIcon={<ArrowRight />}
                    minimal
                    text={formatMessage('APPLY')}
                    onClick={applySegment}
                    type="button"
                    testId="select-segment-button"
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </Card>
  );
};

const emDash = `—`;

export default SegmentCard;
